import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Star, CheckCircle2, Bookmark, Trash2, Calendar, Clock, Film, X, Loader2, RefreshCcw, Search, Pencil } from "lucide-react";
import { toast } from "sonner";

const FALLBACK = "https://images.unsplash.com/photo-1489599849927-2ee91cede3ba?w=600";

export default function MovieDetailDialog({ movie, groups, open, onOpenChange, onChange, onDelete }) {
  const [m, setM] = useState(movie);
  const [tagsInput, setTagsInput] = useState((movie?.tags || []).join(", "));
  const [groupIds, setGroupIds] = useState(movie?.group_ids || []);
  const [notes, setNotes] = useState(movie?.notes || "");
  const [rating, setRating] = useState(movie?.rating || 0);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [rematching, setRematching] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    if (!open || !movie) return;
    setM(movie);
    setTagsInput((movie.tags || []).join(", "));
    setGroupIds(movie.group_ids || []);
    setNotes(movie.notes || "");
    setRating(movie.rating || 0);
  }, [open, movie]);

  if (!m) return null;

  const toggleGroup = (id) => setGroupIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const persist = async (patch) => {
    const res = await api.patch(`/movies/${m.id}`, patch);
    setM(res.data);
    onChange?.(res.data);
    return res.data;
  };

  const toggleWatched = async () => {
    try {
      const res = await api.post(`/movies/${m.id}/toggle-watched`);
      setM(res.data); onChange?.(res.data);
    } catch { toast.error("Failed"); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post(`/movies/${m.id}/refresh`);
      if (res.data.updated) {
        setM(res.data.movie);
        onChange?.(res.data.movie);
        toast.success(`Refreshed: ${res.data.fields.join(", ")}`);
      } else {
        toast("Already up to date");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally { setRefreshing(false); }
  };

  const reIdentify = async () => {
    if (!window.confirm(`Re-identify "${m.title}"? This will drop the current TMDB/IMDB link and search again by title+year. Use this if the genre, director, or year look wrong.`)) return;
    setRematching(true);
    try {
      const res = await api.post(`/movies/${m.id}/refresh`, null, { params: { force_rematch: true } });
      if (res.data.updated) {
        setM(res.data.movie);
        onChange?.(res.data.movie);
        toast.success(`Re-identified — updated: ${res.data.fields.join(", ")}`);
      } else {
        toast("No better match found");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Re-identify failed");
    } finally { setRematching(false); }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const tags = tagsInput.split(",").map(t => t.trim()).filter(Boolean);
      await persist({ tags, group_ids: groupIds, notes, rating: rating || null });
      toast.success("Saved");
      onOpenChange(false);
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Remove "${m.title}"?`)) return;
    try {
      await api.delete(`/movies/${m.id}`);
      onDelete?.(m.id);
      onOpenChange(false);
    } catch { toast.error("Failed"); }
  };

  const startEdit = () => {
    setEditForm({
      title: m.title || "",
      year: m.year || "",
      genre: m.genre || "",
      director: m.director || "",
      runtime: m.runtime || "",
      plot: m.plot || "",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const updates = {};
      ["title", "year", "genre", "director", "runtime", "plot"].forEach(k => {
        if ((editForm[k] || "") !== (m[k] || "")) updates[k] = editForm[k];
      });
      if (Object.keys(updates).length === 0) { setEditing(false); return; }
      const res = await api.patch(`/movies/${m.id}`, updates);
      setM(res.data);
      onChange?.(res.data);
      setEditing(false);
      toast.success("Info updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const toggleSeason = async (n) => {
    try {
      const res = await api.post(`/movies/${m.id}/seasons/${n}/toggle-watched`);
      setM(res.data);
      onChange?.(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const seasons = m.seasons || [];
  const isTV = m.media_type === "tv" && seasons.length > 0;
  const watchedSeasons = seasons.filter(s => s.status === "watched").length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F12] border-white/10 text-slate-100 max-w-3xl p-0 overflow-hidden max-h-[92vh] overflow-y-auto" data-testid="movie-detail-dialog">
        <div className="relative">
          {m.poster && (
            <div className="absolute inset-0 opacity-20 blur-2xl" style={{ backgroundImage: `url(${m.poster})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-[#0F0F12]/80 to-[#0F0F12]" />
          <div className="relative grid md:grid-cols-[200px_1fr] gap-6 p-6">
            <div className="aspect-[2/3] rounded-lg overflow-hidden bg-[#1A1A1E] shadow-2xl mx-auto md:mx-0 max-w-[200px] w-full">
              <img src={m.poster || FALLBACK} alt={m.title} className="w-full h-full object-cover" onError={e => { e.target.src = FALLBACK; }} />
            </div>
            <div className="space-y-3 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {editing ? (
                    <div className="space-y-2" data-testid="detail-edit-form">
                      <Input value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                             placeholder="Title" data-testid="edit-title"
                             className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white text-2xl font-display font-bold h-auto py-2" />
                      <div className="grid grid-cols-2 gap-2">
                        <Input value={editForm.year} onChange={e => setEditForm({ ...editForm, year: e.target.value })}
                               placeholder="Year" data-testid="edit-year"
                               className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white text-xs" />
                        <Input value={editForm.runtime} onChange={e => setEditForm({ ...editForm, runtime: e.target.value })}
                               placeholder="Runtime (e.g. 168 min)" data-testid="edit-runtime"
                               className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white text-xs" />
                      </div>
                      <Input value={editForm.genre} onChange={e => setEditForm({ ...editForm, genre: e.target.value })}
                             placeholder="Genre (comma separated)" data-testid="edit-genre"
                             className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white text-xs" />
                      <Input value={editForm.director} onChange={e => setEditForm({ ...editForm, director: e.target.value })}
                             placeholder="Director" data-testid="edit-director"
                             className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white text-xs" />
                      <Textarea value={editForm.plot} onChange={e => setEditForm({ ...editForm, plot: e.target.value })}
                                placeholder="Plot" rows={3} data-testid="edit-plot"
                                className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white text-xs" />
                      <div className="flex gap-2">
                        <Button onClick={saveEdit} size="sm" data-testid="edit-save" className="bg-[#E11D48] hover:bg-rose-700 rounded-full">Save</Button>
                        <Button onClick={() => setEditing(false)} size="sm" variant="outline" data-testid="edit-cancel" className="bg-transparent border-white/10 rounded-full">Cancel</Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-start gap-2">
                        <h2 className="font-display text-3xl font-bold tracking-tight flex-1">{m.title}</h2>
                        <button onClick={startEdit} data-testid="detail-edit-info" title="Edit title, year, genre, director"
                                className="shrink-0 p-1.5 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 mt-2">
                        {m.year && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{m.year}</span>}
                        {m.runtime && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{m.runtime}</span>}
                        {m.imdb_rating && m.imdb_rating !== "N/A" && parseFloat(m.imdb_rating) > 0 && <span className="flex items-center gap-1"><Star className="w-3 h-3 text-[#F59E0B] fill-[#F59E0B]" />{Number(parseFloat(m.imdb_rating).toFixed(1))}</span>}
                        {m.media_type === "tv" && <span className="px-1.5 py-0.5 rounded bg-white/10 text-[10px]">TV</span>}
                      </div>
                      {m.genre && <div className="text-xs text-slate-500 mt-1">{m.genre}</div>}
                      {m.director && <div className="text-xs text-slate-500">Dir. {m.director}</div>}
                    </>
                  )}
                </div>
              </div>

              {!editing && m.plot && <p className="text-sm text-slate-300 leading-relaxed">{m.plot}</p>}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button onClick={toggleWatched} data-testid="detail-toggle-watched" className={`rounded-full ${m.status === "watched" ? "bg-white/10 hover:bg-white/20" : "bg-[#E11D48] hover:bg-rose-700"}`}>
                  {m.status === "watched" ? <><Bookmark className="w-4 h-4 mr-1.5" /> Move to backlog</> : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Mark watched</>}
                </Button>
                <Button variant="outline" onClick={refresh} disabled={refreshing} data-testid="detail-refresh" className="rounded-full bg-transparent border-white/10 text-slate-200 hover:bg-white/10">
                  <RefreshCcw className={`w-4 h-4 mr-1.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh metadata
                </Button>
                <Button variant="outline" onClick={reIdentify} disabled={rematching} data-testid="detail-reidentify" className="rounded-full bg-transparent border-white/10 text-amber-300 hover:bg-amber-500/10"
                        title="Wrong movie? Re-search TMDB by title+year">
                  <Search className={`w-4 h-4 mr-1.5 ${rematching ? "animate-spin" : ""}`} /> Re-identify
                </Button>
                <Button variant="outline" onClick={remove} data-testid="detail-delete" className="rounded-full bg-transparent border-white/10 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300">
                  <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 space-y-5 border-t border-white/5 pt-5 bg-[#0F0F12] relative">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Your rating</div>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(n => (
                <button key={n} onClick={() => setRating(n === rating ? 0 : n)} data-testid={`star-${n}`}
                  className="p-0.5 transition-transform hover:scale-110">
                  <Star className={`w-6 h-6 ${n <= rating ? "fill-[#F59E0B] text-[#F59E0B]" : "text-slate-600"}`} />
                </button>
              ))}
              {rating > 0 && <button onClick={() => setRating(0)} className="text-xs text-slate-500 ml-2 self-center">clear</button>}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Groups</div>
            <div className="flex flex-wrap gap-2">
              {groups.length === 0 && <div className="text-sm text-slate-500">No groups yet.</div>}
              {groups.map(g => (
                <button key={g.id} onClick={() => toggleGroup(g.id)} data-testid={`detail-toggle-group-${g.id}`}
                  className={`px-3 py-1 rounded-full text-xs border transition-all ${
                    groupIds.includes(g.id) ? "bg-[#E11D48] border-[#E11D48] text-white" : "bg-transparent border-white/10 text-slate-300 hover:border-white/30"
                  }`}>{g.name}</button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Tags</div>
            <Input value={tagsInput} onChange={e => setTagsInput(e.target.value)} placeholder="comma, separated" data-testid="detail-tags" className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white" />
          </div>

          <div>
            <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Notes</div>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Why this movie? Personal thoughts…" rows={3} data-testid="detail-notes" className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white" />
          </div>

          {isTV && (
            <div data-testid="seasons-section">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-400">
                  Seasons <span className="text-slate-500 normal-case ml-1">· {watchedSeasons}/{seasons.length} watched</span>
                </div>
              </div>
              <div className="space-y-2">
                {seasons.map(s => {
                  const watched = s.status === "watched";
                  return (
                    <button
                      key={s.season_number}
                      onClick={() => toggleSeason(s.season_number)}
                      data-testid={`season-toggle-${s.season_number}`}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                        watched ? "bg-[#10B981]/10 border-[#10B981]/30" : "bg-[#1A1A1E] border-white/5 hover:border-white/20"
                      }`}
                    >
                      {s.poster ? (
                        <img src={s.poster} alt="" className="w-10 h-14 object-cover rounded" />
                      ) : (
                        <div className="w-10 h-14 bg-white/5 rounded flex items-center justify-center text-xs text-slate-500">
                          S{s.season_number}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium flex items-center gap-2">
                          {s.name || `Season ${s.season_number}`}
                          {watched && <CheckCircle2 className="w-4 h-4 text-[#10B981]" />}
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-3">
                          {s.episode_count ? <span>{s.episode_count} eps</span> : null}
                          {s.air_date ? <span>· {s.air_date.slice(0, 4)}</span> : null}
                        </div>
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${watched ? "bg-[#10B981]/20 text-[#10B981]" : "bg-white/5 text-slate-400"}`}>
                        {watched ? "Watched" : "Backlog"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <Button onClick={saveAll} disabled={saving} data-testid="detail-save" className="w-full bg-[#E11D48] hover:bg-rose-700 h-11">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
